import type { WidgetProps } from 'app/schema-form/types';
import { type ChangeEvent, type ChangeEventHandler, type MouseEventHandler, type ReactNode, useCallback } from 'react';
import { getWidgetId, useWidgetTitle } from '../../../../schema-form/util/widget.js';

export type SingleLineTextWidgetOwnProps = WidgetProps & {
  multiline?: boolean;
  clearable?: boolean;
  size?: 'small' | 'medium';
  endAdornment?: ReactNode;
};

export type SingleLineTextWidgetHookProps = {
  id: string;
  label: string;
  value: string;
  description?: string;
  instructions?: string;
  maxLength?: number;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  readOnly?: boolean;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  handleClear?: MouseEventHandler<HTMLButtonElement>;
};

export const useSingleLineText = (props: SingleLineTextWidgetOwnProps): SingleLineTextWidgetHookProps => {
  const { onChange, schema, config, value } = props;

  const label = useWidgetTitle(props);
  const id = getWidgetId(props);
  const description = schema.get('description');
  const maxLength = schema.get('maxLength');
  const instructions = config.get('instructions');
  const rows = config.get('rows');
  const minRows = config.get('minRows');
  const maxRows = config.get('maxRows');
  const readOnly = props.readOnly ?? config.get('readOnly');

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  return {
    ...props,
    id,
    label,
    value: String(value),
    description,
    instructions,
    rows,
    minRows,
    maxRows,
    maxLength,
    readOnly,
    handleChange,
    handleClear
  };
};
